import apiClient from './client';

export interface Document {
  id: number;
  filename: string;
  file_url?: string;
  google_drive_file_id?: string;
  google_drive_file_url?: string;
  file_size: number;
  mime_type: string;
  uploaded_by: {
    id: number;
    username: string;
    email: string;
    first_name?: string;
    last_name?: string;
  };
  uploaded_at: string;
  category_name: string;
  category_id: number;
  category_group?: string;
  category_group_label?: string;
  submission_id: number;
  status?: string;
}

/** Documents grouped by category group → category (control) → files */
export interface DocumentsByStructure {
  group_code: string;
  group_label: string;
  categories: Array<{
    category_id: number;
    category_name: string;
    files: Document[];
  }>;
}

export interface GroupedDocument {
  date: string;
  users: Array<{
    user: {
      id: number | null;
      username: string;
      email: string | null;
      first_name?: string;
    };
    files: Document[];
  }>;
}

export const documentsApi = {
  getAll: async (
    uploadedBy?: number,
    dateFrom?: string,
    dateTo?: string,
    category?: number
  ): Promise<Document[]> => {
    const params: any = {};
    if (uploadedBy) params.uploaded_by = uploadedBy;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (category) params.category = category;
    
    const response = await apiClient.get('/files/', { params });
    return response.data.results || response.data;
  },

  getGrouped: async (
    uploadedBy?: number,
    dateFrom?: string,
    dateTo?: string,
    category?: number
  ): Promise<GroupedDocument[]> => {
    const params: any = {};
    if (uploadedBy) params.uploaded_by = uploadedBy;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (category) params.category = category;
    
    const response = await apiClient.get('/files/grouped/', { params });
    return response.data;
  },

  /** Build structure (group → category → files) from a flat list of documents. */
  buildStructureFromList(files: Document[]): DocumentsByStructure[] {
    const byGroup = new Map<string, { group_label: string; byCategory: Map<number, { category_name: string; files: Document[] }> }>();
    for (const file of files) {
      const groupCode = file.category_group || 'UNCATEGORIZED';
      const groupLabel = file.category_group_label || groupCode;
      if (!byGroup.has(groupCode)) {
        byGroup.set(groupCode, { group_label: groupLabel, byCategory: new Map() });
      }
      const group = byGroup.get(groupCode)!;
      const cid = file.category_id;
      const cname = file.category_name || `Category ${cid}`;
      if (!group.byCategory.has(cid)) {
        group.byCategory.set(cid, { category_name: cname, files: [] });
      }
      group.byCategory.get(cid)!.files.push(file);
    }
    return Array.from(byGroup.entries())
      .sort((a, b) => (a[1].group_label || '').localeCompare(b[1].group_label || ''))
      .map(([group_code, { group_label, byCategory }]) => ({
        group_code,
        group_label,
        categories: Array.from(byCategory.entries())
          .sort((a, b) => (a[1].category_name || '').localeCompare(b[1].category_name || ''))
          .map(([category_id, { category_name, files }]) => ({ category_id, category_name, files })),
      }));
  },

  /** Documents grouped by category group → category (control). Use myAssignments to show only documents in controls assigned to current user. Falls back to list + client-side grouping if by-structure returns 404 (e.g. older backend). */
  getByStructure: async (
    myAssignments?: boolean,
    dateFrom?: string,
    dateTo?: string,
    category?: number,
    uploadedBy?: number,
    status?: string
  ): Promise<DocumentsByStructure[]> => {
    const params: Record<string, string | number> = {
      my_assignments: myAssignments === true ? 'true' : 'false',
    };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (category) params.category = category;
    if (uploadedBy) params.uploaded_by = uploadedBy;
    if (status) params.status = status;
    try {
      const response = await apiClient.get('/files/by-structure/', { params });
      return response.data;
    } catch (err: any) {
      if (err.response?.status === 404) {
        const listParams = { ...params, page_size: 2000 } as Record<string, string | number>;
        const listResponse = await apiClient.get('/files/', { params: listParams });
        const results = listResponse.data.results ?? listResponse.data;
        const list = Array.isArray(results) ? results : [];
        return documentsApi.buildStructureFromList(list);
      }
      throw err;
    }
  },

  getAllUsers: async (): Promise<Array<{ id: number; username: string; email: string; first_name?: string }>> => {
    const response = await apiClient.get('/files/');
    const files: Document[] = response.data.results || response.data;
    const userMap = new Map();
    files.forEach(file => {
      if (file.uploaded_by) {
        userMap.set(file.uploaded_by.id, file.uploaded_by);
      }
    });
    return Array.from(userMap.values());
  },
};

